import { useState } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  SimpleGrid,
  Card,
  Table,
  Badge,
  Button,
  TextInput,
  Group,
  Stack,
  Image,
  Skeleton,
  Divider,
  Alert,
  Anchor,
  Loader,
  Modal,
  Textarea,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconRefresh,
  IconSearch,
  IconShieldCheck,
  IconAlertTriangle,
  IconExternalLink,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import axios from "axios";
import { Endpoints } from "../../../lib/endpoints";
import { usePerformRequest } from "../../../hooks/usePerformRequest";
import PermissionGuard from "../../../components/PermissionGuard";
import { AdminPermissions } from "../../../lib/data";

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("t")}` },
});

const tierColor = (t?: string) =>
  t === "verified" ? "green" : t === "streaming" ? "yellow" : "gray";
// "streaming" tier is shown to staff/members as "Listening" (boss terminology).
const tierLabel = (t?: string) =>
  t === "streaming" ? "Listening" : t === "verified" ? "Verified" : "untiered";
const statusColor = (s?: string) =>
  s === "verified" ? "green" : s === "streaming" ? "yellow" : s === "rejected" ? "red" : s === "manual_review" ? "orange" : "gray";

function StatCard({ label, value, color, onClick, active }: { label: string; value: any; color?: string; onClick?: () => void; active?: boolean }) {
  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      onClick={onClick}
      style={onClick ? { cursor: "pointer", borderColor: active ? "var(--mantine-color-green-6)" : undefined, borderWidth: active ? 2 : undefined } : undefined}
    >
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
      <Text size="xl" fw={700} c={color}>{value ?? 0}</Text>
      {onClick && <Text size="xs" c="dimmed">{active ? "showing below ↓" : "click to view"}</Text>}
    </Card>
  );
}

export default function Verification() {
  // ---- stats ----
  const statsEp = Endpoints.GetTierStats();
  const { data: statsResp, isLoading: statsLoading, reloadData: reloadStats } =
    usePerformRequest<any>({ method: statsEp.method, route: statsEp.route });
  const stats = statsResp?.data;

  // ---- review queue ----
  const reviewsEp = Endpoints.GetVerificationReviews(500);
  const { data: reviewsResp, isLoading: reviewsLoading, reloadData: reloadReviews } =
    usePerformRequest<any>({ method: reviewsEp.method, route: reviewsEp.route });
  const reviews: any[] = reviewsResp?.data ?? [];

  const [busy, setBusy] = useState<string | null>(null);

  // ---- category browser (click a card → list members in that tier) ----
  const [category, setCategory] = useState<string | null>(null);
  const [catRows, setCatRows] = useState<any[]>([]);
  const [catTotal, setCatTotal] = useState(0);
  const [catPage, setCatPage] = useState(1);
  const [catLoading, setCatLoading] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  const loadCategory = async (tier: string, page = 1) => {
    setCategory(tier); setCatPage(page); setCatLoading(true);
    try {
      const ep = Endpoints.GetTierMembers(tier, page);
      const r = await axios.get(ep.route, authHeaders());
      setCatRows(r.data?.data?.rows || []);
      setCatTotal(r.data?.data?.total || 0);
    } catch (e: any) {
      notifications.show({ title: "Load failed", message: e?.response?.data?.message || e.message, color: "red" });
    } finally { setCatLoading(false); }
  };

  const moveMember = async (userId: string, override: string, label: string) => {
    setRowBusy(userId + override);
    try {
      const ep = Endpoints.SetUserOverride(userId);
      await axios.post(ep.route, { override }, authHeaders());
      notifications.show({ title: "Moved", message: `Member → ${label}`, color: "green" });
      reloadStats();
      if (category) await loadCategory(category, catPage);
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally { setRowBusy(null); }
  };

  const refreshAll = () => { reloadStats(); reloadReviews(); if (category) loadCategory(category, catPage); };

  const [rejectTarget, setRejectTarget] = useState<any>(null);
  const [rejectReason, setRejectReason] = useState("");
  const FAKE_PRESET =
    "This account is fake. The follower or post numbers in your screenshot were edited — they don't match your live profile. Uploading a doctored screenshot is not allowed. Verify a real, active account.";
  const REJECT_PRESETS = [
    FAKE_PRESET,
    "The username in your screenshot doesn't match the account you linked. Edit your linked username to match your profile exactly — word for word — then re-verify.",
    "This isn't your own account — show your profile with the “Edit Profile” button visible.",
    "The account doesn't have enough followers yet.",
    "The account doesn't have enough posts yet.",
    "There's no profile picture on the account.",
    "The screenshot was unclear — upload a full, clear screenshot of your profile.",
    "The screenshot looks edited — upload a genuine, unedited screenshot.",
    "This social account is already verified on another VIREL account. Use your own account.",
    "This screenshot was already used on another account. Upload a fresh screenshot of your own profile.",
    "Your account is set to private — make it public (or show your full profile), then re-verify.",
    "We couldn't confirm this is an active, genuine account. Please re-verify with a clear profile screenshot.",
  ];

  const reviewAction = async (socialLinkId: string, decision: "approve" | "reject", reason?: string) => {
    setBusy(socialLinkId + decision);
    try {
      const ep = Endpoints.ReviewSocial(socialLinkId);
      await axios.post(ep.route, { decision, reason }, authHeaders());
      notifications.show({ title: "Done", message: `Verification ${decision}d — member notified`, color: "green" });
      setRejectTarget(null); setRejectReason("");
      refreshAll();
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally { setBusy(null); }
  };

  // Reject the fake verification (with reason) BUT keep the member on Listening —
  // reject alone drops them to untiered (no listening). This honours the boss rule
  // "keep them for listening, block other opportunities": reject + force_streaming.
  const rejectKeepListening = async (row: any, reason: string) => {
    const uid = row?.user?._id || row?.user?.userId;
    setBusy(row.socialLinkId + "reject");
    try {
      await axios.post(Endpoints.ReviewSocial(row.socialLinkId).route, { decision: "reject", reason }, authHeaders());
      if (uid) await axios.post(Endpoints.SetUserOverride(uid).route, { override: "force_streaming" }, authHeaders());
      notifications.show({ title: "Rejected — kept on Listening", message: "Member notified with the reason; can still do Listening, blocked from other opportunities", color: "green" });
      setRejectTarget(null); setRejectReason("");
      refreshAll();
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally { setBusy(null); }
  };

  const resetAction = async (socialLinkId: string) => {
    setBusy(socialLinkId + "reset");
    try {
      const ep = Endpoints.ResetSocialVerification(socialLinkId);
      await axios.post(ep.route, {}, authHeaders());
      notifications.show({ title: "Reset", message: "Member can re-submit", color: "blue" });
      refreshAll();
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally { setBusy(null); }
  };

  // ---- member search + override (fuzzy: type a partial name/email → list) ----
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [member, setMember] = useState<any>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [overrideBusy, setOverrideBusy] = useState<string | null>(null);

  const search = async () => {
    if (!query.trim()) return;
    setLookupLoading(true); setResults([]); setMember(null);
    try {
      const ep = Endpoints.SearchTierUsers(query.trim());
      const r = await axios.get(ep.route, authHeaders());
      const list = r.data?.data || [];
      setResults(list);
      if (list.length === 0)
        notifications.show({ title: "No matches", message: `No members match "${query.trim()}"`, color: "yellow" });
    } catch (e: any) {
      notifications.show({ title: "Search failed", message: e?.response?.data?.message || e.message, color: "red" });
    } finally { setLookupLoading(false); }
  };

  const selectMember = async (idOrEmail: string) => {
    setLookupLoading(true);
    try {
      const ep = Endpoints.GetUserVerification(idOrEmail);
      const r = await axios.get(ep.route, authHeaders());
      setMember(r.data?.data);
    } catch (e: any) {
      notifications.show({ title: "Not found", message: e?.response?.data?.message || e.message, color: "red" });
    } finally { setLookupLoading(false); }
  };

  const setOverride = async (override: string) => {
    if (!member) return;
    setOverrideBusy(override);
    try {
      const id = member.userId || member.email;
      const ep = Endpoints.SetUserOverride(id);
      await axios.post(ep.route, { override }, authHeaders());
      notifications.show({ title: "Override set", message: `${member.email} → ${override}`, color: "green" });
      await selectMember(id);
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally { setOverrideBusy(null); }
  };

  return (
    <PermissionGuard
      permission={AdminPermissions.MANAGE_VERIFICATION}
      fallback={<Container py="xl"><Alert color="red" title="No access">You need the "Manage Verification & Access" permission.</Alert></Container>}
    >
      <Container size="xl" py="md">
        <Modal
          opened={!!rejectTarget}
          onClose={() => { setRejectTarget(null); setRejectReason(""); }}
          title="Reject verification — tell the member why"
          centered
        >
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Pick one or more reasons (they stack) or type your own — the member gets this as a notification and on their verify screen.
            </Text>
            <Stack gap={4}>
              {REJECT_PRESETS.map((p) => (
                <Button
                  key={p}
                  size="xs"
                  variant={rejectReason.split("\n").map((s) => s.trim()).includes(p) ? "filled" : "light"}
                  color={p === FAKE_PRESET ? "red" : "gray"}
                  leftSection={p === FAKE_PRESET ? <IconAlertTriangle size={13} /> : undefined}
                  onClick={() =>
                    setRejectReason((cur) => {
                      const parts = cur.split("\n").map((s) => s.trim()).filter(Boolean);
                      return parts.includes(p) ? parts.filter((x) => x !== p).join("\n") : [...parts, p].join("\n");
                    })
                  }
                  styles={{ inner: { justifyContent: "flex-start" }, label: { whiteSpace: "normal", textAlign: "left" } }}
                >
                  {p}
                </Button>
              ))}
            </Stack>
            <Textarea
              label="Reason (editable)"
              placeholder="Pick one above or type your own"
              autosize
              minRows={2}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.currentTarget.value)}
            />
            <Text size="xs" c="dimmed">
              "Keep Listening" rejects the fake and notifies them, but leaves them able to do Listening
              opportunities (boss rule). "Reject fully" also removes Listening — use only to push an
              account out completely.
            </Text>
            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={() => { setRejectTarget(null); setRejectReason(""); }}>Cancel</Button>
              <Button
                color="red"
                variant="light"
                disabled={!rejectReason.trim()}
                loading={busy === rejectTarget?.socialLinkId + "reject"}
                onClick={() => rejectTarget && reviewAction(rejectTarget.socialLinkId, "reject", rejectReason.trim())}
              >
                Reject fully
              </Button>
              <Button
                color="green"
                disabled={!rejectReason.trim()}
                loading={busy === rejectTarget?.socialLinkId + "reject"}
                onClick={() => rejectTarget && rejectKeepListening(rejectTarget, rejectReason.trim())}
              >
                Reject &amp; keep Listening
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Group justify="space-between" mb="md">
          <div>
            <Title order={2}>Verification & Access</Title>
            <Text c="dimmed" size="sm">Review member verifications, manage tiers and access.</Text>
          </div>
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={refreshAll}>Refresh</Button>
        </Group>

        {/* ── Modes banner ── */}
        {stats?.modes && (
          <Group mb="md" gap="xs">
            <Badge color={stats.modes.social === "enforce" ? "green" : stats.modes.social === "shadow" ? "yellow" : "gray"}>
              Social verify: {stats.modes.social}
            </Badge>
            <Badge color={stats.modes.tierAccess === "enforce" ? "green" : stats.modes.tierAccess === "shadow" ? "yellow" : "gray"}>
              Tier access: {stats.modes.tierAccess}
            </Badge>
            {(stats.modes.social !== "enforce" || stats.modes.tierAccess !== "enforce") && (
              <Text size="xs" c="dimmed">— not yet enforcing on members</Text>
            )}
          </Group>
        )}

        {/* ── Stats ── */}
        {statsLoading ? (
          <Skeleton height={90} mb="lg" />
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 4 }} mb="lg">
            <StatCard label="Verified" value={stats?.userTiers?.verified} color="green" onClick={() => loadCategory("verified")} active={category === "verified"} />
            <StatCard label="Listening" value={stats?.userTiers?.streaming} color="yellow" onClick={() => loadCategory("streaming")} active={category === "streaming"} />
            <StatCard label="Untiered" value={stats?.userTiers?.untiered} onClick={() => loadCategory("untiered")} active={category === "untiered"} />
            <StatCard label="In review" value={stats?.socialVerification?.manual_review} color="orange" />
          </SimpleGrid>
        )}

        {/* ── Category members (click a card above) ── */}
        {category && (
          <Paper withBorder radius="md" p="md" mb="lg">
            <Group justify="space-between" mb="sm">
              <Title order={4} tt="capitalize">
                {tierLabel(category)} members <Badge ml="xs" color={tierColor(category)}>{catTotal}</Badge>
              </Title>
              <Button size="xs" variant="subtle" onClick={() => setCategory(null)}>Close</Button>
            </Group>
            {catLoading ? (
              <Skeleton height={120} />
            ) : catRows.length === 0 ? (
              <Text c="dimmed" py="md" ta="center">No members here.</Text>
            ) : (
              <>
                <Table.ScrollContainer minWidth={640}>
                  <Table verticalSpacing="sm" highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Member</Table.Th>
                        <Table.Th>Tier</Table.Th>
                        <Table.Th>Override</Table.Th>
                        <Table.Th>Move to</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {catRows.map((u: any) => (
                        <Table.Tr key={u.userId}>
                          <Table.Td>
                            <Text size="sm" fw={600}>{u.name}</Text>
                            <Text size="xs" c="dimmed">{u.email}</Text>
                          </Table.Td>
                          <Table.Td><Badge color={tierColor(u.tier)}>{tierLabel(u.tier)}</Badge></Table.Td>
                          <Table.Td>{u.override && u.override !== "none" ? <Badge color={u.override === "banned" ? "red" : "blue"} variant="filled">{u.override}</Badge> : <Text size="xs" c="dimmed">—</Text>}</Table.Td>
                          <Table.Td>
                            <Group gap={6}>
                              {category !== "verified" && (
                                <Button size="xs" color="green" variant="light" loading={rowBusy === u.userId + "force_verified"} onClick={() => moveMember(u.userId, "force_verified", "Verified")}>Verified</Button>
                              )}
                              {category !== "streaming" && (
                                <Button size="xs" color="yellow" variant="light" loading={rowBusy === u.userId + "force_streaming"} onClick={() => moveMember(u.userId, "force_streaming", "Listening")}>Listening</Button>
                              )}
                              {u.override && u.override !== "none" && (
                                <Button size="xs" variant="subtle" loading={rowBusy === u.userId + "none"} onClick={() => moveMember(u.userId, "none", "auto (cleared)")}>Clear</Button>
                              )}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
                {catTotal > catRows.length || catPage > 1 ? (
                  <Group justify="center" mt="sm" gap="xs">
                    <Button size="xs" variant="default" disabled={catPage <= 1 || catLoading} onClick={() => loadCategory(category, catPage - 1)}>← Prev</Button>
                    <Text size="xs" c="dimmed">Page {catPage} · {catTotal} total</Text>
                    <Button size="xs" variant="default" disabled={catPage * 25 >= catTotal || catLoading} onClick={() => loadCategory(category, catPage + 1)}>Next →</Button>
                  </Group>
                ) : null}
              </>
            )}
          </Paper>
        )}

        {/* ── Manual-review queue ── */}
        <Paper withBorder radius="md" p="md" mb="lg">
          <Group justify="space-between" mb="sm">
            <Title order={4}>Manual-review queue {reviews.length > 0 && <Badge ml="xs" color="orange">{reviews.length}</Badge>}</Title>
          </Group>
          {reviewsLoading ? (
            <Skeleton height={120} />
          ) : reviews.length === 0 ? (
            <Group py="lg" justify="center"><IconShieldCheck size={20} color="green" /><Text c="dimmed">Nothing to review — queue is clear.</Text></Group>
          ) : (
            <Table.ScrollContainer minWidth={800}>
              <Table verticalSpacing="sm" striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Screenshot</Table.Th>
                    <Table.Th>Member</Table.Th>
                    <Table.Th>Platform / handle</Table.Th>
                    <Table.Th>Followers / posts</Table.Th>
                    <Table.Th>Why flagged</Table.Th>
                    <Table.Th>Action</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {reviews.map((r) => (
                    <Table.Tr key={r.socialLinkId}>
                      <Table.Td>
                        {r.screenshotUrl ? (
                          <Anchor href={r.screenshotUrl} target="_blank">
                            <Image src={r.screenshotUrl} w={64} h={64} radius="sm" fit="cover" alt="proof" />
                          </Anchor>
                        ) : <Text size="xs" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{r.user?.email || r.user?.username || "—"}</Text>
                        <Text size="xs" c="dimmed">{[r.user?.firstName, r.user?.lastName].filter(Boolean).join(" ")}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" mb={4}>{r.platform}</Badge>
                        {(() => {
                          const norm = (s?: string) => (s || "").replace(/^@/, "").toLowerCase();
                          const mismatch = r.linkedHandle && r.handle && norm(r.linkedHandle) !== norm(r.handle);
                          return (
                            <Stack gap={1}>
                              <Text size="xs" c="dimmed">Linked: <Text span size="sm" c="var(--mantine-color-text)">@{r.linkedHandle || "?"}</Text></Text>
                              <Text size="xs" c="dimmed">Screenshot: <Text span size="sm" c={mismatch ? "orange" : "var(--mantine-color-text)"} fw={mismatch ? 700 : 400}>@{r.handle || "?"}</Text></Text>
                              {mismatch && <Text size="xs" c="orange">⚠ handles differ</Text>}
                              {(r.profileUrl || r.handle) && (
                                <Anchor
                                  href={r.profileUrl || `https://instagram.com/${r.handle}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="xs"
                                  fw={600}
                                >
                                  <Group gap={3} wrap="nowrap"><IconExternalLink size={12} /> View live profile</Group>
                                </Anchor>
                              )}
                            </Stack>
                          );
                        })()}
                      </Table.Td>
                      <Table.Td><Text size="sm">{r.followers ?? "?"} / {r.posts ?? "?"}</Text></Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          {(r.flags || []).map((f: string) => <Badge key={f} size="xs" color="orange" variant="light">{f}</Badge>)}
                          <Text size="xs" c="dimmed" lineClamp={2}>{r.reason}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6}>
                          <Button size="xs" color="green" loading={busy === r.socialLinkId + "approve"} leftSection={<IconCheck size={14} />} onClick={() => reviewAction(r.socialLinkId, "approve")}>Approve</Button>
                          <Button size="xs" color="red" variant="light" loading={busy === r.socialLinkId + "reject"} leftSection={<IconX size={14} />} onClick={() => { setRejectTarget(r); setRejectReason(""); }}>Reject</Button>
                          <Button size="xs" variant="subtle" loading={busy === r.socialLinkId + "reset"} onClick={() => resetAction(r.socialLinkId)}>Reset</Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Paper>

        {/* ── Member lookup + override ── */}
        <Paper withBorder radius="md" p="md">
          <Title order={4} mb="sm">Member override</Title>
          <Group mb="md">
            <TextInput
              placeholder="Type a name or email (e.g. anointed, divine)"
              leftSection={<IconSearch size={16} />}
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              style={{ flex: 1, maxWidth: 420 }}
            />
            <Button onClick={search} loading={lookupLoading}>Search</Button>
          </Group>

          {/* Results — click a member to load their override controls */}
          {!member && results.length > 0 && (
            <Table highlightOnHover mb="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Member</Table.Th>
                  <Table.Th>Tier</Table.Th>
                  <Table.Th>Override</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {results.map((u: any) => (
                  <Table.Tr key={u.userId} style={{ cursor: "pointer" }} onClick={() => selectMember(u.userId)}>
                    <Table.Td>
                      <Text size="sm" fw={600}>{u.name}</Text>
                      <Text size="xs" c="dimmed">{u.email}</Text>
                    </Table.Td>
                    <Table.Td><Badge color={tierColor(u.tier)}>{tierLabel(u.tier)}</Badge></Table.Td>
                    <Table.Td>{u.override && u.override !== "none" ? <Badge color="red" variant="filled">{u.override}</Badge> : <Text size="xs" c="dimmed">—</Text>}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          {lookupLoading && <Loader size="sm" />}
          {member && (
            <Stack gap="sm">
              <Group justify="space-between">
                <Group>
                  <Text fw={600}>{member.email}</Text>
                  <Badge color={tierColor(member.tier)}>{tierLabel(member.tier)}</Badge>
                  {member.override && member.override !== "none" && <Badge color="red" variant="filled">{member.override}</Badge>}
                </Group>
                {results.length > 0 && (
                  <Button size="xs" variant="subtle" onClick={() => setMember(null)}>← Back to results</Button>
                )}
              </Group>

              {(member.socials || []).length > 0 && (
                <Table withRowBorders={false}>
                  <Table.Tbody>
                    {member.socials.map((s: any, i: number) => (
                      <Table.Tr key={i}>
                        <Table.Td><Badge variant="light">{s.platform}</Badge></Table.Td>
                        <Table.Td>@{s.handle || "?"}</Table.Td>
                        <Table.Td><Badge color={statusColor(s.status)}>{s.status}</Badge></Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{s.followers ?? "?"} followers · {s.posts ?? "?"} posts</Text></Table.Td>
                        <Table.Td>{(s.flags || []).map((f: string) => <Badge key={f} size="xs" color="orange" variant="light" mr={4}>{f}</Badge>)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}

              <Divider label="Set override" labelPosition="left" />
              <Group>
                <Button color="green" variant="light" loading={overrideBusy === "force_verified"} onClick={() => setOverride("force_verified")}>Force Verified</Button>
                <Button color="yellow" variant="light" loading={overrideBusy === "force_streaming"} onClick={() => setOverride("force_streaming")}>Force Listening</Button>
                <Button color="red" leftSection={<IconAlertTriangle size={14} />} loading={overrideBusy === "banned"} onClick={() => setOverride("banned")}>Ban</Button>
                <Button variant="subtle" loading={overrideBusy === "none"} onClick={() => setOverride("none")}>Clear override</Button>
              </Group>
            </Stack>
          )}
        </Paper>
      </Container>
    </PermissionGuard>
  );
}
